import { RateLimiter } from "@shared/utils/rate-limiter";
import { Auth } from "@shared/Auth";
import { TreeNode } from "@shared/ui-native/Tree";
import { IModelInfo } from "./types";
import { Ajax2 } from "@shared/Ajax";


export interface TileInfo {
    tileCount: number;
    maxZoom: number;
    timestamp: number;
    rasterUri: string;
    vectorUri: string;
}

export interface DrawingInfo {
    projectUuid: string;
    fileUuid: string;
    tileInfo?: TileInfo;
    geojsonObjectKeys: string[];
}


export interface IModelStatus {
    project_uuid: string;
    uuid: string;
    available: boolean
    simplified_available: boolean
    pointcloud_available: boolean
    mesh_image_available: boolean
    processing_started: number,
    processing_completed: number,
    has_failed: boolean,
    is_in_processing: boolean
}



const RATE_LIMITER = new RateLimiter(5, 7);


const getVizApiBaseUrl = (): string => {

    const isLocal: boolean = kuura.getServerDomain().includes("localhost");

    if (isLocal) {
        return "http://localhost:8001/viz/v1";
    }

    const isBeta: boolean = kuura.getServerDomain().includes("beta");
    return isBeta ?
    kuura.AwsApiGatewayDomain() + "/viz/beta/v1"
    : kuura.AwsApiGatewayDomain() + "/viz/v1";
}


const getModelLayerCounts = async (modelUuid: string): Promise<ILayerCounts | null> => {
    const response = await Ajax2({
        url: `${kuura.getContextPath()}/api/1/model/${modelUuid}`
    })


    if (!response.status) {
        return null;
    }

    const modelData = response["model"];

    if (!modelData) {
        return null;
    }

    const layerCounts = modelData["layerCounts"];

    if (!layerCounts) {
        return null;
    }

    return layerCounts;
}

const getModelInfoByHeadUuid = async (headUuid: string): Promise<IModel | null> => {
    const response = await Ajax2({
        url: `${kuura.getContextPath()}/api/1/model/head/${headUuid}`
    })

    if (!response.status) {
        return null;
    }

    const modelData = response["model"];

    if (!modelData) {
        return null;
    }

    return modelData;
}

const modelHasBeenProcessedInvalidly = (status: IModelStatus): boolean => {
    return status.available && (!status.simplified_available || !status.pointcloud_available || !status.mesh_image_available);
}

const meshImageHasFailed = (status: IModelStatus, processingStartTime?: Date): boolean => {

    if (processingStartTime) {
        const processingDuration = new Date().getTime() - processingStartTime.getTime();
        if (processingDuration > 15 * 60 * 1000) {
            return status.available && !status.mesh_image_available; // Processing took too long
        } else {
            return false; // Processing is still ongoing
        }
    }

    return status.available && !status.mesh_image_available;
}

const getModelStatus = async (node: TreeNode): Promise<IModelStatus> => {

    const tokenReturn = await RATE_LIMITER.requestToken();
    const baseUrl = getVizApiBaseUrl();
    const url = `${baseUrl}/${kuura.getProjectUuid()}/${node.uuid}/status`
    const token = await Auth.getToken();

    try {
        const resp = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token.accessToken}`
            }
        });

        const json = await resp.json();
        return json;

    } catch (e) {
        console.warn("Could not find model status for", node.text);
        return null;
    } finally {
        tokenReturn();
    }
}


const getDrawingStatus = async (node: TreeNode): Promise<any> => {
    const tokenReturn = await RATE_LIMITER.requestToken();
    const baseUrl = getVizApiBaseUrl();
    const url = `${baseUrl}/drawings/${kuura.getProjectUuid()}/${node.uuid}/status`
    const token = await Auth.getToken();

    try {
        const resp = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token.accessToken}`
            }
        });

        const json = await resp.json();
        return json;

    } catch (e) {
        console.warn("Could not find model status for", node.text);
        return null;
    } finally {
        tokenReturn();
    }
}


const getPMTilesUrl = async (node: TreeNode): Promise<string | null> => {
    const tokenReturn = await RATE_LIMITER.requestToken();
    const baseUrl = getVizApiBaseUrl();
    const url = `${baseUrl}/drawings/${kuura.getProjectUuid()}/${node.uuid}/pmtiles-url`
    const token = await Auth.getToken();

    try {
        const resp = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token.accessToken}`
            }
        });


        if (!resp.ok) {
            return null;
        }

        const json = await resp.json();
        return json["url"];
    } catch (e) {
        console.warn("Could not find model status for", node.text);
        return null;
    } finally {
        tokenReturn();
    }
}

const getModelInfo = async (node: TreeNode): Promise<IModelInfo> => {

    const tokenReturn = await RATE_LIMITER.requestToken();
    const baseUrl = getVizApiBaseUrl();
    const url = `${baseUrl}/${kuura.getProjectUuid()}/${node.uuid}/info`
    const token = await Auth.getToken();


    try {
        const resp = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token.accessToken}`
            }
        });



        const json = await resp.json();
        return json;

    } catch (e) {
        console.warn("Could not find model info for", node.text);
        return null;
    } finally {
        tokenReturn();
    }
}


const getDrawingInfo = async (node: TreeNode): Promise<DrawingInfo> => {

    const tokenReturn = await RATE_LIMITER.requestToken();
    const baseUrl = getVizApiBaseUrl();
    const url = `${baseUrl}/drawings/${kuura.getProjectUuid()}/${node.uuid}/info`
    const token = await Auth.getToken();


    try {
        const resp = await fetch(url, {
            headers: {
                'Authorization': `Bearer ${token.accessToken}`
            }
        });



        const json = await resp.json();
        return json;

    } catch (e) {
        console.warn("Could not find drawing info for", node.text);
        return null;
    } finally {
        tokenReturn();
    }
}


const pollModelUntilReady = (node: TreeNode): Promise<IModelStatus> => {
    return new Promise((resolve, reject) => {
        const pollInterval = setInterval(async () => {
            const status: IModelStatus = await getModelStatus(node);
            if (status.processing_completed || status.has_failed) {
                clearInterval(pollInterval);
                resolve(status);
            }
        }, 20 * 1000);
    });


}


export const ModelService = {
    getModelInfo,
    getModelStatus,
    getVizApiBaseUrl,
    getDrawingInfo,
    modelHasBeenProcessedInvalidly,
    meshImageHasFailed,
    pollModelUntilReady,
    getModelLayerCounts,
    getModelInfoByHeadUuid,
    getDrawingStatus,
    getPMTilesUrl
}
